import { Link } from 'react-router-dom';
import { useAuth } from '@repletoryreach/sdk-auth';
import { Stack, Card, Group, Text, Badge, SimpleGrid } from '@repletoryreach/sdk-ui';
import { useT } from '../../i18n/I18nProvider';
import { SETTINGS_NAV, settingsLinkShown } from './nav';
import { useIsOrgAdmin } from '../../app/components/RoleGate';
import { useSmsStatus } from '../../lib/sms';
import { useEmailStatus } from '../../lib/email';
import './settings-hub.css';

/**
 * The settings hub.
 *
 * `settings.md` A4:
 * - the nine cards were a second hand-maintained copy of the shell's nav and
 *   the two had already diverged (item 3); both read `SETTINGS_NAV` now;
 * - Billing was missing entirely, though it is exactly what someone opening
 *   Settings to find an invoice is looking for (item 4);
 * - the org-wide / personal distinction the shell nav makes was thrown away
 *   (item 5);
 * - the "Email unverified" and "2FA off" badges told the tenant something was
 *   wrong and offered no way to fix it (items 1 and 2).
 */
export default function SettingsHubPage() {
  const t = useT();
  const isAdmin = useIsOrgAdmin();
  const { available: smsAvailable } = useSmsStatus();
  const { available: emailAvailable } = useEmailStatus();
  const { user } = useAuth();

  // Both flags come back on the session, so this needs no extra call. They
  // default to "fine" while the session is still loading, so the page does not
  // flash a warning at a user who has neither problem.
  const emailVerified = user?.email_verified ?? true;
  const twoFactorOn = user?.two_factor_enabled ?? true;

  return (
    <Stack gap="lg">
      {/* Both badges are links now: each one is a single tap from its fix. */}
      {(!emailVerified || !twoFactorOn) && (
        <Group gap="xs" wrap="wrap">
          {!emailVerified && (
            <Link to="/auth/verify-email" className="rr-settings-hub__alert">
              <Badge size="sm" color="orange" variant="light">{t('settings.hub.emailUnverified')}</Badge>
            </Link>
          )}
          {!twoFactorOn && (
            <Link to="/settings/security" className="rr-settings-hub__alert">
              <Badge size="sm" color="orange" variant="light">{t('settings.hub.twoFactorOff')}</Badge>
            </Link>
          )}
        </Group>
      )}

      {SETTINGS_NAV.map((section) => {
        // Agents do not see owner-and-admin settings (2 October 2026).
        const items = section.items.filter((i) => !i.hubHidden && settingsLinkShown(i, isAdmin, { sms: smsAvailable, email: emailAvailable }));
        if (items.length === 0) return null;
        return (
          <Stack key={section.labelKey} gap="sm">
            {/* A4 item 5: the grouping the shell nav makes was discarded here. */}
            <Text role="overline" c="var(--rr-color-text-muted)">{t(section.labelKey)}</Text>
            {/* A4 item 6: a bare `cols={2}` is two cramped columns on a phone. */}
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
              {items.map((item) => (
                /* A4 item 7: only the title text was clickable, and it was
                   inline-styled rather than a Text role. */
                <Link key={item.path} to={item.path} className="rr-settings-hub__card">
                  <Card withBorder>
                    <Stack gap={4}>
                      <Text role="body" fw={600}>{t(item.labelKey)}</Text>
                      <Text role="caption" c="var(--rr-color-text-muted)">{t(item.descriptionKey)}</Text>
                    </Stack>
                  </Card>
                </Link>
              ))}
            </SimpleGrid>
          </Stack>
        );
      })}
    </Stack>
  );
}
